import {
    BackgroundType,
    Database,
    DBChangeEvent,
    DBChangeEventAction,
    DBChangeListener,
    HistoryStatus,
    NoteSearchOptions
} from "../types";
import {NoteData, NoteObject} from "../objects/NoteObject";
import {createContext, ReactNode, useContext, useEffect, useRef, useState} from "react";
import {randomUUID} from "../lib";
import {NotePageObject} from "../objects/NotePageObject";
import {TextContentObject} from "../objects/TextContentObject";
import {useTranslation} from "react-i18next";
import NoteFolder, {NoteFolderData} from "../objects/NoteFolder";

const IndexedDBContext = createContext<{
    initialized: boolean, setInitialized: (initialized: boolean) => void
    changeListeners: DBChangeListener[], setChangeListeners: (listeners: DBChangeListener[]) => void
    db: IDBDatabase|null, setDB: (db: IDBDatabase) => void
    encryptionPassword: string|null, setEncryptionPassword: (password: string|null) => void
}>({
    initialized: false, setInitialized: () => false,
    changeListeners: [], setChangeListeners: () => null,
    db: null, setDB: () => null,
    encryptionPassword: null, setEncryptionPassword: () => null,
})

export interface DBModel {}

const IndexedDBProvider = (props: { children: ReactNode}) => {
    const [ initialized, setInitialized ] = useState<boolean>(false)
    const [ changeListeners, setChangeListeners ] = useState<DBChangeListener[]>([])
    const [ db, setDB ] = useState<IDBDatabase|null>(null)
    const [ encryptionPassword, setEncryptionPassword ] = useState<string|null>(null)

    return (
        <IndexedDBContext.Provider value={{
            initialized, setInitialized,
            changeListeners, setChangeListeners,
            db, setDB,
            encryptionPassword, setEncryptionPassword,
        }}>
            { props.children }
        </IndexedDBContext.Provider>
    )
}

const connect = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = window.indexedDB.open('NotesAppDatabase', 6)

        request.onerror = (ev: any) => {
            reject(ev.target.error?.message)
        }

        request.onsuccess = (ev: any) => {
            const db = ev.target.result
            resolve(db)
        }

        request.onupgradeneeded = (ev: IDBVersionChangeEvent) => {
            const request = ev.target as IDBOpenDBRequest
            const transaction = request.transaction as IDBTransaction

            const db: IDBDatabase = request.result

            db.onerror = (ev: any) => {
                reject(ev.target.error?.message)
            }

            console.log('version', ev.newVersion)
            console.log('ev', ev)

            const migrations = [
                () => {
                    console.log('starting migration to version', 1)
                    const notesStore = db.createObjectStore('note', {
                        keyPath: 'id',
                    })
                    notesStore.createIndex('title', 'title', { unique: false })
                    notesStore.createIndex('previewImage', 'previewImage', { unique: false })

                    const notePageStore = db.createObjectStore('notePage', {
                        keyPath: 'id',
                    })
                    notePageStore.createIndex('noteId', 'noteId', { unique: false })
                    notePageStore.createIndex('backgroundType', 'backgroundType', { unique: false })
                    notePageStore.createIndex('backgroundColor', 'backgroundColor', { unique: false })

                    const notePathStore = db.createObjectStore('notePath', {
                        keyPath: 'id',
                    })
                    notePathStore.createIndex('pageId', 'pageId', { unique: false })
                    notePathStore.createIndex('color', 'color', { unique: false })
                    notePathStore.createIndex('points', 'points', { unique: false })

                    const noteTextContent = db.createObjectStore('noteTextContent', {
                        keyPath: 'id',
                    })
                    noteTextContent.createIndex('pageId', 'pageId', { unique: true })
                    noteTextContent.createIndex('content', 'content', { unique: false })
                    noteTextContent.createIndex('style', 'style', { unique: false })
                },
                () => {
                    console.log('starting migration to version', 2)
                    const notesStore = transaction.objectStore('note')
                    notesStore.createIndex('meta', 'meta', { unique: false })

                    const notesPageStore = transaction.objectStore('notePage')
                    notesPageStore.createIndex('order', 'order', { unique: false })
                    notesPageStore.createIndex('meta', 'meta', { unique: false })
                },
                () => {
                    console.log('starting migration to version', 3)
                    const notesStore = transaction.objectStore('note')
                    notesStore.createIndex('deleted', 'deleted', { unique: false })
                },
                () => {
                    db.deleteObjectStore('notePage')
                    db.deleteObjectStore('notePath')
                    db.deleteObjectStore('noteTextContent')
                },
                () => {
                    const notePageStore = db.createObjectStore('noteCategory', {
                        keyPath: 'id',
                    })
                    notePageStore.createIndex('parentId', 'parentId', { unique: false })
                    notePageStore.createIndex('title', 'title', { unique: false })

                    const notesStore = transaction.objectStore('note')
                    notesStore.createIndex('categoryId', 'categoryId', { unique: false })
                },
                () => {
                    db.deleteObjectStore('noteCategory')

                    const noteFolderStore = db.createObjectStore('noteFolder', {
                        keyPath: 'id',
                    })
                    noteFolderStore.createIndex('parentId', 'parentId', { unique: false })
                    noteFolderStore.createIndex('order', 'order', { unique: false })
                    noteFolderStore.createIndex('title', 'title', { unique: false })

                    const notesStore = transaction.objectStore('note')
                    notesStore.deleteIndex('categoryId')
                    notesStore.createIndex('folderId', 'folderId', { unique: false })
                }
            ]

            const version = ev.newVersion as number

            for (let i = 0; i < version; i++) {
                try {
                    migrations[i]()
                } catch (e) {
                    console.warn('failed to execute migration', i, e)
                }
            }
        }
    })
}

export function useIndexedDB(): Database {
    const { t } = useTranslation()

    const {
        initialized, setInitialized,
        changeListeners, setChangeListeners,
        db, setDB,
        encryptionPassword, setEncryptionPassword,
    } = useContext(IndexedDBContext)

    const listenersRef = useRef(changeListeners)
    listenersRef.current = changeListeners

    useEffect(() => {
        ;(async () => {
            const db = await connect()
            setDB(db)
            setInitialized(true)
        })()
    }, [])

    const fireEvent = (table: string, action: DBChangeEventAction, object: DBModel) => {
        const listeners = listenersRef.current.filter(l => {
            return (!l.tables || l.tables.includes(table)) &&
                   (!l.actions || l.actions.includes(action))
        })

        // console.log('fire', table, action, 'to', listeners.length, 'out of', listenersRef.current.length, 'listeners', listeners)

        listeners.forEach(l => {
            l.onChange({
                table,
                action,
                object,
            })
        })
    }

    const createNewNote = async (data: { folderId?: string } = {}): Promise<NoteObject> => {
        return new Promise(async (resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            const transaction = db.transaction(['note'], 'readwrite')
            transaction.onerror = (e: Event) => {
                // @ts-ignore
                reject('failed to create transaction: ' + e.target?.error)
            }

            const noteId = randomUUID()
            const pageId = randomUUID()

            const now = new Date()

            const note = new NoteObject({
                ...data,
                id: noteId,
                title: t('newNoteName'),
                pages: [
                    new NotePageObject({
                        id: pageId,
                        noteId: noteId,
                        backgroundType: BackgroundType.LINES1,
                        backgroundColor: '#fff',
                        order: 0,
                        meta: {
                            created: new Date(),
                        }
                    })
                ],
                textContents: [
                    new TextContentObject({
                        id: randomUUID(),
                        pageId,
                        content: '',
                        style: {}
                    })
                ],
                meta: {
                    created: now,
                    lastModified: now,
                }
            })

            transaction.oncomplete = () => {
                fireEvent('note', 'create', note)
                resolve(note)
            }

            const noteStore = transaction.objectStore('note')
            noteStore.add(note)
        })
    }

    const getNote = async (noteId: string): Promise<NoteObject> => {
        return new Promise((resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            const transaction = db.transaction(['note'], 'readonly')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            const noteStore = transaction.objectStore('note')
            const request = noteStore.get(noteId)

            request.onerror = (ev: Event) => {
                reject('failed to retrieve note')
            }

            request.onsuccess = async (ev: Event) => {
                resolve(new NoteObject(request.result))
            }
        })
    }

    const defaultNoteSearchOptions: NoteSearchOptions = {
        deleted: false,
    }

    const getNotes = async (where: NoteSearchOptions = {}): Promise<NoteObject[]> => {
        where = { ...defaultNoteSearchOptions, ...where }

        const notes: NoteObject[] = await new Promise((resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            const transaction = db.transaction(['note'], 'readonly')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            const noteStore = transaction.objectStore('note')
            const request = noteStore.openCursor()

            request.onerror = (ev: Event) => {
                reject('failed to retrieve note')
            }

            const notes: NoteObject[] = []
            request.onsuccess = async (ev: Event) => {
                const cursor = (ev.target as IDBRequest)?.result as IDBCursorWithValue | null;

                if (cursor) {
                    const data = cursor.value as NoteData

                    if (where.folderId && data.folderId !== where.folderId) {
                        cursor.continue()
                        return
                    }

                    if (!where.deleted && data.deleted) {
                        cursor.continue()
                        return
                    }

                    if (where.deleted && !data.deleted) {
                        cursor.continue()
                        return
                    }

                    notes.push(new NoteObject(data))
                    cursor.continue()
                    return
                }

                resolve(notes)
            }
        })

        for (const note of notes) {
            if (!note.previewImage) {
                await note.createPreviewImage()
                await updateNote(note)
            }
        }

        return notes
    }

    const updateNote = async (note: NoteObject, keepTimestamp = false): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            await note.createPreviewImage(true)

            console.log('saving', encryptionPassword)
            if (encryptionPassword) {
                note = await note.encrypt(encryptionPassword)
            }

            const transaction = db.transaction(['note'], 'readwrite')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            if (!keepTimestamp) {
                note.meta.lastModified = new Date()
            }

            transaction.oncomplete = (e: Event) => {
                fireEvent('note', 'update', note)
                resolve()
            }

            const noteStore = transaction.objectStore('note')
            noteStore.put(note)
        })
    }

    const deleteNote = async (noteId: string, permanent: boolean = false): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            const note = await getNote(noteId)

            if (note.encrypted && encryptionPassword) {
                await note.decrypt(encryptionPassword)
            }

            const transaction = db.transaction(['note'], 'readwrite')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            transaction.oncomplete = (e: Event) => {
                resolve()
            }

            const noteStore = transaction.objectStore('note')

            if (!permanent) {
                note.folderId = ''
                note.deleted = new Date()
                fireEvent('note', 'update', note)
                await updateNote(note)
                return
            }

            console.log('deleting note', noteId)
            noteStore.delete(noteId)

            fireEvent('note', 'delete', note)
        })
    }

    const deleteNotes = async (noteIds: string[], permanent: boolean = false): Promise<void> => {
        await Promise.all(noteIds.map(id => {
            return deleteNote(id, permanent)
        }))
    }

    const getFolder = async (folderId: string): Promise<NoteFolder> => {
        return new Promise((resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            const transaction = db.transaction(['noteFolder'], 'readonly')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            const noteFolderStore = transaction.objectStore('noteFolder')
            const request = noteFolderStore.get(folderId)

            request.onerror = (ev: Event) => {
                reject('failed to retrieve note')
            }

            request.onsuccess = async (ev: Event) => {
                resolve(new NoteFolder(request.result))
            }
        })
    }

    const getFolders = async (parentId: string | null = null): Promise<NoteFolder[]> => {
        return new Promise((resolve, reject) => {
            if (!db) {
                throw new Error('db not initialized')
            }

            const transaction = db.transaction(['noteFolder'], 'readonly')

            transaction.addEventListener('error', e => {
                    console.error(e)
                    reject('failed to create transaction')
                })

            const request = transaction.objectStore('noteFolder')
                .index('parentId')
                .getAll(parentId || '')

            request.addEventListener('error', e => {
                console.error(e)
                reject(e)
            })

            request.addEventListener('success', () => {
                const objects = request.result as NoteFolderData[]
                resolve(objects.map(d => new NoteFolder(d)))
            })
        })
    }

    const getFoldersRecursive = async (parentId: string | null = null): Promise<NoteFolder[]> => {
        const folders = await getFolders(parentId)
        for (const folder of folders) {
            const children = await getFoldersRecursive(folder.id)
            if (children) {
                folder.children = children
            }
        }

        return folders.sort((a, b) => a.order > b.order ? 1 : -1)
    }

    const createFolder = async (title: string, parentId: string | null = null): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            let currentFolders = await getFolders(parentId)
            const existingOrders = currentFolders.map(f => f.order)
            const max = existingOrders.length ? Math.max(...existingOrders) : -1

            const folder = {
                id: randomUUID(),
                title,
                parentId: parentId || '',
                order: max + 1,
            }

            const transaction = db.transaction(['noteFolder'], 'readwrite')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            transaction.oncomplete = (e: Event) => {
                fireEvent('noteFolder', 'create', folder)
                resolve()
            }

            const noteFolderStore = transaction.objectStore('noteFolder')
            noteFolderStore.put(folder)
        })
    }

    const updateFolder = async (folder: NoteFolder): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!db) {
                throw new Error('database not initialized')
            }

            const transaction = db.transaction(['noteFolder'], 'readwrite')
            transaction.onerror = (e: Event) => {
                console.error(e)
                reject('failed to create transaction')
            }

            transaction.oncomplete = (e: Event) => {
                fireEvent('noteFolder', 'update', folder)
                resolve()
            }

            const noteFolderStore = transaction.objectStore('noteFolder')
            noteFolderStore.put(folder)
        })
    }

    return {
        initialized,
        createNewNote,
        notes: [],
        getNote,
        getNotes,
        updateNote,
        deleteNote,
        deleteNotes,

        getFolder,
        getFolders,
        getFoldersRecursive,
        createFolder,
        updateFolder,

        addEventListener: (tables: string[] | string | null, actions: DBChangeEventAction[] | null, onChange: (event: DBChangeEvent) => void) => {
            if (typeof tables === 'string') {
                tables = [tables]
            }
            // console.log('add listener', tables, actions, listenersRef.current.length)
            setChangeListeners([
                ...listenersRef.current,
                {
                    tables,
                    actions,
                    onChange,
                }
            ])
        },

        setEncryptionPassword,

        removeEventListener: (onChange: (event: DBChangeEvent) => void) => {
            setChangeListeners(listenersRef.current.filter(l => l.onChange !== onChange))
        },

        createHistorySnapshot: (noteId: string) => null,
        noteHistory: [],
        historyStatusForNote: (noteId: string) => ({} as HistoryStatus),
        historyGoBack: (noteId: string) => null,
        historyGoForward: (noteId: string) => null,
    }
}

export {
    IndexedDBContext,
    useIndexedDB as default,
    IndexedDBProvider,
}